import { type Component } from "dreamland/core";
import { router } from "dreamland/router";
import { auth, ApiError } from "../api";
import { AUTH_CONFIG } from "../config";

type AuthStatus = "loading" | "success" | "error";

const AuthCallback: Component<
	{},
	{
		status: AuthStatus;
		error: string | null;
	}
> = function (cx) {
	this.status = "loading";
	this.error = null;

	cx.mount = async () => {
		try {
			// Handle the OAuth redirect - extract code and exchange for token
			await auth.handleRedirect(AUTH_CONFIG.redirectUri);

			// Verify credentials and get user info
			await auth.verifyCredentials();

			this.status = "success";

			// Navigate to home after a brief delay to show success
			setTimeout(() => {
				router.navigate("/home");
			}, 500);
		} catch (err) {
			this.status = "error";

			if (err instanceof ApiError) {
				this.error = err.message;
			} else if (err instanceof Error) {
				this.error = err.message;
			} else {
				this.error = "An unexpected error occurred during authentication.";
			}

			console.error("Auth callback error:", err);
		}
	};

	const LoadingView = () => (
		<>
			<h1>Authenticating...</h1>
			<p>Please wait while we complete the login process.</p>
			<div class="loading-spinner" />
		</>
	);

	const SuccessView = () => (
		<>
			<h1>Success!</h1>
			<p>You have been logged in. Redirecting...</p>
		</>
	);

	const ErrorView = () => (
		<>
			<h1>Authentication Failed</h1>
			<p class="error-message">{use(this.error)}</p>
			<button class="primary" on:click={() => router.navigate("/login")}>
                <span class="material-symbols-sharp">refresh</span>
				Try Again
			</button>
			<button class="secondary" on:click={() => router.navigate("/")}>
                <span class="material-symbols-sharp">home</span>
				Go Home
			</button>
		</>
	);

	return (
		<div class="auth-page">
			<div class="auth-container">
				{use(this.status).andThen((status: AuthStatus) => {
					switch (status) {
						case "loading":
							return <LoadingView />;
						case "success":
							return <SuccessView />;
						case "error":
							return <ErrorView />;
					}
				})}
			</div>
		</div>
	);
};

export default AuthCallback;
